import React, { Component } from 'react';
import { compose } from 'redux';
import { injectIntl } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';
import * as validators from '../../util/validators';
import { Form, PrimaryButton, FieldTextInput, FieldSelect } from '../../components';
import counties from './Counties.js';
import config from '../../config';
import ReCAPTCHA from 'react-google-recaptcha';
import css from './ExpressionOfInterestForm.module.css';

class ExpressionOfInterestFormComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      recaptchaValue: null,
      submitError: null,
      isSubmitting: false,
    };
    this.submittedValues = {};
    this.recaptchaRef = React.createRef();
    this.handleSubmit = this.handleSubmit.bind(this);
    this.onRecaptchaChange = this.onRecaptchaChange.bind(this);
  }

  onRecaptchaChange(value) {
    this.setState({ recaptchaValue: value, submitError: null });
  }

  async handleSubmit(values) {
    const { recaptchaValue } = this.state;

    if (!recaptchaValue) {
      this.setState({ submitError: 'Please complete the reCAPTCHA verification' });
      return;
    }

    this.setState({ isSubmitting: true, submitError: null });
    this.submittedValues = values;

    try {
      const response = await fetch('/api/send-eoi-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...values,
          recaptchaToken: recaptchaValue,
        }),
      });

      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error('Server returned an invalid response. Please try again.');
      }

      const result = await response.json();

      if (response.ok && result.success) {
        this.props.onSubmit({ success: true });
        this.setState({ isSubmitting: false });
      } else {
        throw new Error(result.error || 'Failed to submit expression of interest');
      }
    } catch (error) {
      console.error('Error submitting EOI:', error);
      this.setState({
        submitError: error.message || 'Failed to submit. Please try again.',
        isSubmitting: false,
      });

      // Reset reCAPTCHA on error
      if (this.recaptchaRef.current) {
        this.recaptchaRef.current.reset();
        this.setState({ recaptchaValue: null });
      }
    }
  }

  render() {
    const { expressionSent } = this.props;

    if (expressionSent) {
      return (
        <div role="alert" aria-live="polite" aria-atomic="true">
          <p className={css.successMessage}>
            Thank you for reaching out, we'll be in touch soon
          </p>
        </div>
      );
    }

    return (
      <FinalForm
        {...this.props}
        onSubmit={this.handleSubmit}
        render={fieldRenderProps => {
          const {
            rootClassName = null,
            className = null,
            handleSubmit,
            inProgress = false,
            intl,
            invalid,
            ready,
            someError = null,
            form,
            values,
          } = fieldRenderProps;
          // No `ensureCurrentUser()` because we rely on server API checks for CRM-only data amends.

          // todo counterparts of this for all fields
          // const newPasswordRequired = validators.requiredStringNoTrim(newPasswordRequiredMessage);

          const genericFailure = someError ? (
            <span className={css.error}>Please ensure all fields are filled in correctly.</span>
          ) : null;

          const submittedOnce = Object.keys(this.submittedValues).length > 0;
          const pristineSinceLastSubmit = submittedOnce && isEqual(values, this.submittedValues);
          const classes = classNames(rootClassName || css.root, className);
          const submitDisabled = invalid || pristineSinceLastSubmit || inProgress || expressionSent;

          const firstNameLabel = intl.formatMessage({
            id: 'ExpressionOfInterestForm.firstNameLabel',
          });
          const firstNamePlaceholder = intl.formatMessage({
            id: 'ExpressionOfInterestForm.firstNamePlaceholder',
          });
          const firstNameRequiredMessage = intl.formatMessage({
            id: 'ExpressionOfInterestForm.firstNameRequired',
          });
          const firstNameRequired = validators.required(firstNameRequiredMessage);

          // lastName
          const lastNameLabel = intl.formatMessage({
            id: 'ExpressionOfInterestForm.lastNameLabel',
          });
          const lastNamePlaceholder = intl.formatMessage({
            id: 'ExpressionOfInterestForm.lastNamePlaceholder',
          });
          const lastNameRequiredMessage = intl.formatMessage({
            id: 'ExpressionOfInterestForm.lastNameRequired',
          });
          const lastNameRequired = validators.required(lastNameRequiredMessage);

          //email
          const emailLabel = intl.formatMessage({
            id: 'ExpressionOfInterestForm.emailLabel',
          });
          const emailPlaceholder = intl.formatMessage({
            id: 'ExpressionOfInterestForm.emailPlaceholder',
          });
          const emailRequiredMessage = intl.formatMessage({
            id: 'ExpressionOfInterestForm.emailRequired',
          });
          const emailRequired = validators.required(emailRequiredMessage);
          const emailInvalidMessage = intl.formatMessage({
            id: 'ExpressionOfInterestForm.emailInvalid',
          });
          const emailValid = validators.emailFormatValid(emailInvalidMessage);

          const countyRequiredMessage = intl.formatMessage({
            id: 'ExpressionOfInterestForm.countyRequired',
          });
          const countyRequired = validators.required(countyRequiredMessage);

          return (
            <Form className={classes} onSubmit={handleSubmit}>
              <div>
                <h3 className={css.editFormMessage}>
                  Interested in becoming a member of Signalise in future? Please fill out the form
                  below:
                </h3>
                <div className={css.name}>
                  <FieldTextInput
                    className={css.firstNameRoot}
                    type="text"
                    id="firstname"
                    name="firstname"
                    autoComplete="given-name"
                    label={firstNameLabel}
                    placeholder={firstNamePlaceholder}
                    validate={firstNameRequired}
                  />
                  <FieldTextInput
                    className={css.lastNameRoot}
                    type="text"
                    id="lastname"
                    name="lastname"
                    autoComplete="family-name"
                    label={lastNameLabel}
                    placeholder={lastNamePlaceholder}
                    validate={lastNameRequired}
                  />
                  <FieldTextInput
                    type="email"
                    id="email"
                    name="email"
                    autoComplete="email"
                    label={emailLabel}
                    placeholder={emailPlaceholder}
                    validate={validators.composeValidators(emailRequired, emailValid)}
                  />
                </div>

                <FieldTextInput
                  type="text"
                  className={css.field}
                  id="city"
                  name="city"
                  label="City (optional)"
                />

                <FieldSelect
                  className={css.field}
                  id="state"
                  name="state"
                  label="County"
                  validate={countyRequired}
                >
                  <option disabled value="">
                    Choose from...{' '}
                  </option>
                  {counties.map((a, i) => {
                    return (
                      <option key={i} value={a}>
                        {a}
                      </option>
                    );
                  })}
                </FieldSelect>
                <FieldSelect
                  className={css.field}
                  id="userType"
                  name="userType"
                  label="I am interested in joining Signalise (optional)"
                >
                  <option disabled value="">
                    Choose from...
                  </option>
                  <option value="commProf">as a Communication Professional</option>
                  <option value="customer">to book services</option>
                  <option value="deafUser">as a Deaf person</option>
                </FieldSelect>
                <FieldTextInput
                  type="text"
                  className={css.field}
                  id="reason"
                  name="reason"
                  label="Reason for Interest (optional)"
                />
              </div>
              <div className={css.bottomWrapper}>
                {genericFailure}
                {this.state.submitError && (
                  <p className={css.error}>{this.state.submitError}</p>
                )}

                <ReCAPTCHA
                  ref={this.recaptchaRef}
                  sitekey={config.recaptchaSiteKey}
                  onChange={this.onRecaptchaChange}
                />

                <PrimaryButton
                  type="submit"
                  className={css.primaryButtonRoot}
                  inProgress={inProgress || this.state.isSubmitting}
                  ready={ready}
                  disabled={submitDisabled || this.state.isSubmitting}
                >
                  Contact us
                </PrimaryButton>
              </div>
            </Form>
          );
        }}
      />
    );
  }
}

const ExpressionOfInterestForm = compose(injectIntl)(ExpressionOfInterestFormComponent);
ExpressionOfInterestForm.displayName = 'ExpressionOfInterestForm';

export default ExpressionOfInterestForm;
